import * as React from "react";
import {Link} from "react-router-dom";
import {RouteComponentProps, withRouter} from "react-router";
import {api} from "../../../api/api";
import AttachmentApi from "../../../api/AttachmentApi";
import {
	EvChargingQuarter,
	EvChargingQuarterBill,
	EvChargingQuarterMeterPoint,
	EvChargingQuarterMonth
} from "../../../api/EvChargingApi";
import DataGrid from "../../../hiberUiKit/src/organisms/DataGrid/DataGrid";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import ButtonSecondary from "../../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import {CommonDetailsStatusRow} from "../../accounting/common/CommonDetailsStatusRow";
import {CommonDetailsSummary} from "../../accounting/common/CommonDetailsSummary";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import Loading from "../../ui/loading/Loading";
import Routes from "../../../navigation/route/Route";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import {
	formatCredits,
	formatDate,
	formatKwh,
	formatMprn,
	formatPeriod,
	lateBillsLabel,
	meterAndJobCountLabel,
	monthLabel,
	quarterLabel,
	quarterStateLabel
} from "../evChargingBill";
import {evColors, evMessageStyles} from "../evChargingStyles";

type Props = RouteComponentProps<{ quarterId: string }>;

/**
 * Evidence files listed before the rest of a job's hide behind "+N more": a bill with screenshots stays one line or
 * two, not a column of fifteen names.
 */
const SHOWN_FILES = 2;

interface State {
	quarter?: EvChargingQuarter;
	failed: boolean;
	/**
	 * Set when the address names no quarter, which refreshing cannot fix.
	 */
	notFound: boolean;
	/**
	 * Meters whose bills are shown, by {@link rowKey}: a meter billed in several months has a row in each. Those with
	 * a late bill start open, since that bill is why the totals moved.
	 */
	expanded: string[];
	/**
	 * Jobs whose evidence is listed in full; a long list otherwise shows its first files.
	 */
	allFilesShown: string[];
	/**
	 * The quarter whose ZIP is on its way, so a download still finishing for a quarter left meanwhile does not free
	 * the button of the one shown.
	 */
	downloadingId?: string;
	/**
	 * Months whose CSV is on their way, by {@link monthDownloadKey}, for the same reason.
	 */
	downloadingMonths: string[];
}

/**
 * Which meter row of which month, as kept in {@link State.expanded}.
 */
function rowKey(month: EvChargingQuarterMonth, meterPoint: EvChargingQuarterMeterPoint): string {
	return `${month.month}/${meterPoint.meterPointId}`;
}

function monthDownloadKey(quarterId: string, month: string): string {
	return `${quarterId}/${month}`;
}

/**
 * One quarter split into its months, as NORA's portal takes a Statement of Supply per month: under each month a row
 * per MPRN, each opening to the bills it adds up, so every number in a month's file leads to a bill. Laid out like
 * the accounting details pages: a status row, the table, and the quarter's totals under it.
 */
class EvChargingQuarterPage extends React.Component<Props, State> {
	state: State = {failed: false, notFound: false, expanded: [], allFilesShown: [], downloadingMonths: []};

	private unmounted = false;

	/**
	 * Numbers each load, so only the latest answer lands, even when the address goes back to an earlier quarter.
	 */
	private loads = 0;

	componentDidMount() {
		this.load();
	}

	componentDidUpdate(previous: Props) {
		// the route keeps the page mounted when only the quarter in the address changes
		if (previous.match.params.quarterId !== this.props.match.params.quarterId) {
			this.setState({quarter: undefined, failed: false, notFound: false, expanded: [], allFilesShown: []});
			this.load();
		}
	}

	private load() {
		const load = ++this.loads;
		// an answer for a quarter left meanwhile must not show under another one's address
		const current = () => !this.unmounted && load === this.loads;
		api.instance.evChargingApi.getQuarter(this.props.match.params.quarterId)
			.then(quarter => {
				if (!current()) return;
				this.setState({
					quarter,
					expanded: quarter.months.reduce((keys: string[], month) => keys.concat(month.meterPoints
						.filter(m => m.bills.some(b => b.late))
						.map(m => rowKey(month, m))), [])
				});
			})
			.catch(error => {
				if (!current()) return;
				// a mistyped or outdated link: 400 for an id that is not one, 404 for one that names no quarter
				const status = error.response && error.response.status;
				if (status === 400 || status === 404) {
					this.setState({failed: true, notFound: true});
					return;
				}
				this.setState({failed: true});
				notifyBugsnagAndShowToast(error, "Fetch quarter");
			});
	}

	componentWillUnmount() {
		this.unmounted = true;
	}

	render() {
		const {quarter, failed, notFound} = this.state;
		const summary = quarter && quarter.summary;
		const title = summary ? quarterLabel(summary.year, summary.quarter) : "Quarter";
		return (
			<div className={"l-page"}>
				<CommonDetailsStatusRow icon={Icon.Quote}
										title={title}
										onBackClick={this.backToQuarters}
										refNumPath={[{label: "RTFO credit submissions", onClick: this.backToQuarters}, title]}
										status={summary && quarterStateLabel(summary.state)}
										additionalComponent={summary && this.renderDownload()}/>
				<Loading isLoading={!quarter && !failed}>
					{failed
						? <div style={{...evMessageStyles.error, ...styles.message}}>
							{notFound
								? "There is no such quarter. Go back to RTFO credit submissions and pick one from the list."
								: "The quarter could not be loaded. Refresh the page to try again."}
						</div>
						: quarter && this.renderQuarter(quarter)}
				</Loading>
			</div>
		);
	}

	/**
	 * On the title's line at the right, where Bill and Transaction put their actions.
	 */
	private renderDownload() {
		return (
			<div className={"c-common-details_buttons_container"}>
				<div className={"c-common-details_buttons_last-button"}>
					{/* disabled rather than spinning: the spinner narrows the button */}
					<ButtonPrimary label={"Download ZIP"} onClick={this.download} disabled={this.downloading()}/>
				</div>
			</div>
		);
	}

	private renderQuarter(quarter: EvChargingQuarter) {
		const {summary} = quarter;
		return (
			<>
				{/* what the quarter is, as a line under its title; what it adds up to goes under the table */}
				<div style={styles.subtitle}>
					{formatPeriod(summary.firstDay, summary.lastDay)} · NORA deadline {formatDate(summary.submissionDeadline)}
					{summary.lateBillCount > 0 &&
					<span style={{...evMessageStyles.chip, marginLeft: 12}}>{lateBillsLabel(summary.lateBillCount)}</span>}
				</div>
				<DataGrid hasHover={true}>
					<thead>
					<tr className={"c-common-details_table-header-row"}>
						<th className={"c-common-details_table-desc-cell"}>MPRN</th>
						<th>METER NAME</th>
						<th>PARTNER BRAND</th>
						<th className={"t-right"}>JOBS</th>
						<th className={"t-right"} style={styles.numberColumn}>ENERGY (KWH)</th>
						<th className={"t-right"} style={styles.numberColumn}>CREDITS</th>
					</tr>
					</thead>
					<tbody>
					{/* a quarter outlives its bills when their jobs are deleted */}
					{quarter.months.length === 0 &&
					<tr><td colSpan={6} style={styles.empty}>No jobs in this quarter.</td></tr>}
					{quarter.months.map(month => this.renderMonth(quarter, month))}
					</tbody>
				</DataGrid>
				{/* the totals under the lines they add up, as on Bill and Invoice */}
				<div className={"c-common-details_common-summary_external-container"}>
					<CommonDetailsSummary items={[
						{label: "Meters", value: String(summary.meterPointCount)},
						{label: "Jobs", value: String(summary.billCount)},
						{label: "Energy (kWh)", value: formatKwh(summary.totalKwh)}
					]}
										  totalItem={{label: "Credits", value: formatCredits(summary.totalCredits)}}
										  style={styles.summary}/>
				</div>
			</>
		);
	}

	/**
	 * A month's header carries what the month is and its file; its numbers are on the meter rows under it, and on a
	 * total row only when there are several meters to add up.
	 */
	private renderMonth(quarter: EvChargingQuarter, month: EvChargingQuarterMonth) {
		const label = monthLabel(month.month);
		return (
			<React.Fragment key={month.month}>
				<tr style={styles.monthRow}>
					<td colSpan={6} className={"c-common-details_table-desc-cell"} style={styles.monthCell}>
						<div style={styles.monthHeader}>
							<i className={"fa fa-fw fa-calendar"} aria-hidden={true}/>
							<span style={styles.monthName}>{label}</span>
							<span style={styles.monthMeta}>{meterAndJobCountLabel(month.meterPointCount, month.billCount)}</span>
							{month.lateBillCount > 0 && <span style={evMessageStyles.chip}>{lateBillsLabel(month.lateBillCount)}</span>}
							<span style={styles.monthAction}>
								<ButtonSecondary label={"Download CSV"} onClick={() => this.downloadMonth(quarter, month.month)}
												 disabled={this.downloadingMonth(quarter, month.month)}/>
							</span>
						</div>
					</td>
				</tr>
				{month.meterPoints.map(meterPoint => this.renderMeterPoint(month, meterPoint))}
				{month.meterPointCount >= 2 &&
				<tr className={"c-common-details_table-row font-bold"} style={styles.totalRow}>
					<td colSpan={3} className={"c-common-details_table-desc-cell"}>Total {label}</td>
					<td className={"t-right"}>{month.billCount}</td>
					<td className={"t-right"}>{formatKwh(month.totalKwh)}</td>
					<td className={"t-right"}>{formatCredits(month.totalCredits)}</td>
				</tr>}
			</React.Fragment>
		);
	}

	private renderMeterPoint(month: EvChargingQuarterMonth, meterPoint: EvChargingQuarterMeterPoint) {
		const key = rowKey(month, meterPoint);
		const open = this.state.expanded.indexOf(key) >= 0;
		const lateCount = meterPoint.bills.filter(b => b.late).length;
		const toggle = () => this.toggle(key);
		return (
			<React.Fragment key={key}>
				<tr className={"c-common-details_table-row clickable"} onClick={toggle}>
					<td className={"c-common-details_table-desc-cell"} style={{whiteSpace: "nowrap"}}>
						{/* the row opens on a click anywhere; the button is there for the keyboard */}
						<button type={"button"} className={"t-burgundy-color"} style={styles.toggle} aria-expanded={open}
								onClick={e => {
									e.stopPropagation();
									toggle();
								}}>
							<i className={`fa fa-fw ${open ? "fa-caret-down" : "fa-caret-right"}`} aria-hidden={true}/>
							{formatMprn(meterPoint.mprn)}
						</button>
						{lateCount > 0 && <span style={{...evMessageStyles.chip, marginLeft: 8}}>{lateBillsLabel(lateCount)}</span>}
					</td>
					<td>{meterPoint.nickname || <span style={{color: evColors.muted}}>-</span>}</td>
					<td>{meterPoint.partnerBrands.map(pb => pb.name).join(", ")}</td>
					<td className={"t-right"}>{meterPoint.bills.length}</td>
					<td className={"t-right"}>{formatKwh(meterPoint.totalKwh)}</td>
					<td className={"t-right"}><b>{formatCredits(meterPoint.totalCredits)}</b></td>
				</tr>
				{open &&
				<tr className={"is-details-row"}>
					<td colSpan={4} className={"c-common-details_table-desc-cell"} style={styles.caption}>
						<div style={styles.slots}><span>Period</span><span>Job</span><span>Evidence</span><span>Entered</span></div>
					</td>
					<td style={styles.caption}/>
					<td style={styles.caption}/>
				</tr>}
				{open && meterPoint.bills.map(bill => this.renderBill(meterPoint, bill))}
			</React.Fragment>
		);
	}

	private renderBill(meterPoint: EvChargingQuarterMeterPoint, bill: EvChargingQuarterBill) {
		// only worth naming when the meter changed hands during the quarter
		const owner = meterPoint.partnerBrands.length > 1
			? meterPoint.partnerBrands.filter(pb => pb.id === bill.partnerBrandId).map(pb => pb.name)[0]
			: undefined;
		return (
			<tr className={"is-details-row"} key={bill.jobId}>
				<td colSpan={4} className={"c-common-details_table-desc-cell"} style={styles.billCell}>
					<div style={styles.slots}>
						<span>
							{formatPeriod(bill.periodFrom, bill.periodTo)}
							{owner && <span style={{color: evColors.muted}}> · {owner}</span>}
						</span>
						<span><Link to={Routes.EditJob(bill.jobReferenceNumber)} className={"t-link"}>{bill.jobReferenceNumber}</Link></span>
						<span style={styles.files}>
							{bill.files.length === 0 && <span style={{color: evColors.muted}}>-</span>}
							{this.shownFiles(bill).map(file =>
								<a key={file.attachmentId} href={AttachmentApi.getAttachmentDownloadUrl(file.attachmentId)}
								   target={"_blank"} rel={"noopener noreferrer"} className={"t-link"} style={styles.file}
								   title={file.name}>{file.name}</a>)}
							{this.hiddenFileCount(bill) > 0 &&
							<button type={"button"} className={"t-link"} style={styles.moreFiles}
									onClick={() => this.setState(state => ({allFilesShown: [...state.allFilesShown, bill.jobId]}))}>
								+{this.hiddenFileCount(bill)} more
							</button>}
						</span>
						<span>
							{formatDate(bill.enteredOn)}
							{bill.late && <span style={{...evMessageStyles.chip, marginLeft: 8}}>Added after deadline</span>}
						</span>
					</div>
				</td>
				<td className={"t-right"} style={styles.billCell}>{formatKwh(bill.kwh)}</td>
				<td className={"t-right"} style={styles.billCell}>{formatCredits(bill.credits)}</td>
			</tr>
		);
	}

	private shownFiles(bill: EvChargingQuarterBill) {
		const all = bill.files.length <= SHOWN_FILES + 1 || this.state.allFilesShown.indexOf(bill.jobId) >= 0;
		return all ? bill.files : bill.files.slice(0, SHOWN_FILES);
	}

	private hiddenFileCount(bill: EvChargingQuarterBill): number {
		return bill.files.length - this.shownFiles(bill).length;
	}

	private toggle(key: string) {
		this.setState(state => ({
			expanded: state.expanded.indexOf(key) >= 0
				? state.expanded.filter(k => k !== key)
				: [...state.expanded, key]
		}));
	}

	private backToQuarters = () => this.props.history.push(Routes.EvChargingQuarters);

	private downloading(): boolean {
		const {quarter, downloadingId} = this.state;
		return !!quarter && quarter.summary.id === downloadingId;
	}

	private download = () => {
		const {quarter} = this.state;
		if (!quarter || this.downloading()) return;
		const id = quarter.summary.id;
		const done = () => !this.unmounted &&
			this.setState(state => state.downloadingId === id ? {downloadingId: undefined} : {});
		this.setState({downloadingId: id});
		api.instance.evChargingApi.downloadStatementOfSupply(quarter.summary)
			.then(done)
			.catch(error => {
				done();
				notifyBugsnagAndShowToast(error, "Download Statement of Supply");
			});
	};

	private downloadingMonth(quarter: EvChargingQuarter, month: string): boolean {
		return this.state.downloadingMonths.indexOf(monthDownloadKey(quarter.summary.id, month)) >= 0;
	}

	private downloadMonth(quarter: EvChargingQuarter, month: string) {
		if (this.downloadingMonth(quarter, month)) return;
		const key = monthDownloadKey(quarter.summary.id, month);
		const done = () => !this.unmounted &&
			this.setState(state => ({downloadingMonths: state.downloadingMonths.filter(k => k !== key)}));
		this.setState(state => ({downloadingMonths: [...state.downloadingMonths, key]}));
		api.instance.evChargingApi.downloadMonthStatementOfSupply(quarter.summary, month)
			.then(done)
			.catch(error => {
				done();
				notifyBugsnagAndShowToast(error, "Download month Statement of Supply");
			});
	}
}

// RestrictedRoute renders the page without the router's props
export default withRouter(EvChargingQuarterPage);

const styles: { [key: string]: React.CSSProperties } = {
	// lines up with the title above, as the accounting pages do
	message: {margin: "32px 144px"},
	empty: {textAlign: "center", padding: "48px 0"},
	// continues the white status row above it, under the title's text, which starts at 176px
	subtitle: {background: "white", padding: "0 144px 24px 176px", fontSize: 14, color: evColors.muted},
	// right edge on the CREDITS column's figures, which the grid pads by 17px
	summary: {marginRight: 17, marginBottom: 48},
	numberColumn: {width: 130},
	// a shade darker than the page, so a month reads as a heading over its meters
	monthRow: {background: "#e9ebf3", borderTop: "1px solid #d5d8e4"},
	monthCell: {paddingTop: 11, paddingBottom: 11},
	monthHeader: {display: "flex", alignItems: "center", gap: 14},
	monthName: {fontSize: 15, fontWeight: 700},
	// darker than evColors.muted, which is too faint on the month's background
	monthMeta: {color: "#5a5f6e"},
	monthAction: {marginLeft: "auto"},
	totalRow: {borderTop: "2px solid #cfd3e0"},
	toggle: {
		border: 0, background: "transparent", padding: 0, font: "inherit", fontWeight: 700, cursor: "pointer",
		fontVariantNumeric: "tabular-nums"
	},
	// indented by the caret's width so the bills line up under the MPRN digits; columns sized to the longest values:
	// "1 May – 31 May 2026", "K7P2QX4M-1", a file name, a date with its marker
	slots: {
		display: "grid", gridTemplateColumns: "190px 120px minmax(0, 1fr) 250px", columnGap: 16, alignItems: "center",
		marginLeft: "1.28em"
	},
	caption: {paddingTop: 8, paddingBottom: 0, fontSize: 12, color: evColors.muted},
	billCell: {paddingTop: 10, paddingBottom: 10},
	files: {display: "flex", flexDirection: "column", minWidth: 0},
	// a long file name ends in an ellipsis instead of pushing the date out
	file: {display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"},
	moreFiles: {border: 0, background: "transparent", padding: 0, font: "inherit", textAlign: "left", cursor: "pointer"}
};
